import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import './assets/styles/main.scss'; // Import the main SCSS file

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Auth Components
import { AuthProvider, useAuth } from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import Home from './pages/Home';
import Setup from './pages/Setup';
import TutorSession from './pages/TutorSession';
import Contact from './pages/Contact';
import UserGuide from './pages/UserGuide';
import PitchPage from './pages/PitchPage';
import ParentPortal from './pages/ParentPortal';
import ExamPrep from './pages/ExamPrep';
import HomeworkHelp from './pages/HomeworkHelp';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import EarlyAccess from './pages/EarlyAccess';
import { DynamoUtilsProvider } from './context/DynamoUtilsContext';
import { BedrockClientProvider } from './context/BedrockClientContext';
import { SessionProvider } from './context/SessionContext';

// Context for managing focused mode state
export const FocusedModeContext = React.createContext({
  isFocusedMode: false,
  setFocusedMode: () => {},
});

// Main App with session management
const AppContent = () => {
  // Get authentication status directly from AuthProvider
  const { tokenValidationComplete } = useAuth();
  const location = useLocation();
  
  // State to track if we're in focused mode (active tutor session)
  const [isFocusedMode, setFocusedMode] = useState(false);
  
  // Detect if we're on the tutor page
  useEffect(() => {
    // If we're on the tutor page, check query params for session
    if (location.pathname === '/tutor') {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session');
      
      // If there's a session ID, we might be in an active session
      // The TutorSession component will call setFocusedMode(true) when a session is active
      if (!sessionId) {
        // If no session ID, we're likely on the setup page
        setFocusedMode(false);
      }
    } else {
      // Not on tutor page, make sure we're not in focused mode
      setFocusedMode(false);
    }
  }, [location.pathname, location.search]);
  
  // Show a minimal loading state while validating tokens
  if (!tokenValidationComplete) {
    return (
      <div className="App">
        <div className="auth-loading-container">
          <div className="auth-loading-spinner"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }
  
  // Render the full app once token validation is complete
  return (
    <FocusedModeContext.Provider value={{ isFocusedMode, setFocusedMode }}>
      <div className={`App ${isFocusedMode ? 'focused-mode' : ''}`}>
        {/* Only render the Header in non-tutor pages. The TutorSession component handles its own header */}
        {location.pathname !== '/tutor' && <Header />}
        <main className="App-main">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/UserGuide" element={<UserGuide />} />
            <Route path="/pitch" element={<PitchPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Navigate to="/early-access" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/early-access" element={<EarlyAccess />} />
            
            {/* Protected routes - require authentication */}
            <Route path="/setup" element={
              <ProtectedRoute>
                <Setup />
              </ProtectedRoute>
            } />
            <Route path="/tutor" element={
              <ProtectedRoute>
                <TutorSession />
              </ProtectedRoute>
            } />
            <Route path="/parent" element={
              <ProtectedRoute>
                <ParentPortal />
              </ProtectedRoute>
            } />
            <Route path="/exam-prep" element={
              <ProtectedRoute>
                <ExamPrep />
              </ProtectedRoute>
            } />
            <Route path="/homework-help" element={
              <ProtectedRoute>
                <HomeworkHelp />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
        {/* Conditionally render footer only when NOT in focused mode and NOT on exam prep page */}
        {!isFocusedMode && location.pathname !== '/exam-prep' && <Footer />}
      </div>
    </FocusedModeContext.Provider>
  );
};


function App() {
  return (
    <AuthProvider>
      <SessionProvider>
        <DynamoUtilsProvider>
          <BedrockClientProvider>
            <Router>
              <AppContent />
            </Router>
          </BedrockClientProvider>
        </DynamoUtilsProvider>
      </SessionProvider>
    </AuthProvider>
  );
}

export default App;
